<script lang="ts">
import 'datatables.net';

import type { PropType } from 'vue';
import {
    defineComponent,
    onBeforeUnmount,
    onMounted,
    shallowRef,
} from 'vue';

import { type TablePropsType, useDatatable } from './data-table-internal';

export default defineComponent({
    props: {
        id: {
            type: String,
            default: undefined,
        },
        state: {
            type: Object as PropType<TablePropsType['state']>,
            default: undefined,
        },
        processing: {
            type: Boolean,
            default: true,
        },
        ordering: {
            type: Boolean,
            default: true,
        },
        searching: {
            type: Boolean,
            default: true,
        },
        search: {
            type: String,
            default: undefined,
        },
        isDataEqual: {
            type: Function as PropType<TablePropsType['isDataEqual']>,
            default: undefined,
        },
        selectType: {
            type: String as PropType<TablePropsType['selectType']>,
            default: undefined,
        },
        hasChildren: {
            type: Function as PropType<TablePropsType['hasChildren']>,
            default: undefined,
        },
        columns: {
            type: Array as PropType<TablePropsType['columns']>,
            required: true,
        },
        reactiveColums: {
            type: Boolean,
            default: undefined,
        },
        ajax: {
            type: Function as PropType<TablePropsType['ajax']>,
            default: undefined,
        },
        data: {
            type: Array as PropType<TablePropsType['data']>,
            default: undefined,
        },
        initialOrder: {
            type: Array as PropType<TablePropsType['initialOrder']>,
            default: undefined,
        },
        reactiveOrder: {
            type: Boolean,
            default: undefined,
        },
        lengthMenu: {
            type: Array as PropType<TablePropsType['lengthMenu']>,
            default: undefined,
        },
        customized: {
            type: Boolean,
            default: true,
        },
        scrollX: {
            type: Boolean,
            default: true,
        },
        chevrons: {
            type: Boolean,
            default: false,
        },
        colvis: {
            type: Object as PropType<TablePropsType['colvis']>,
            default: undefined,
        },
        updateType: {
            type: Number as PropType<TablePropsType['updateType']>,
            default: undefined,
        },
    },
    emits: [
        'update:search',
        'update:selection',
        'update:initialOrder',
        'click-td',
        'draw',
        'after-init',
        'before-destroy',
        'colvis-state-change',
    ],
    setup(props, { emit, expose }) {
        const tableRef = shallowRef<HTMLElement | undefined>(undefined);
        const {
            createDataTable,
            destroyDataTable,
            offUnmount,
            publicApi,
        } = useDatatable(tableRef, props, emit);

        onMounted(() => {
            createDataTable();
        });
        onBeforeUnmount(() => {
            destroyDataTable();
            offUnmount.forEach((off) => off?.());
        });
        expose(publicApi);

        return {
            tableRef,
        };
    },
});
</script>
<template>
    <div :id="id">
        <!-- because it is v-once, without wrapper it looses position after redraw -->
        <table v-once ref="tableRef" class="display">
            <slot />
        </table>
    </div>
</template>
